import { useEffect, useRef, useState } from "react";

export const STEP_OPTIONS = [1, 3, 5, 10];
export const RATE_OPTIONS = [0.25, 0.5, 1, 1.5, 2];

export const TransportBar = ({ playing, onTogglePlay, coarseStepSec, onCoarseStepChange, onStep, onFrame,
  playbackRate = 1, onRateChange, volume = 1, onVolumeChange, onFullscreen, onPointerComplete }) => {
  const [rateOpen, setRateOpen] = useState(false);
  const rateRef = useRef(null);
  const triggerRef = useRef(null);
  const pointerMenu = useRef(false);
  const volumePointer = useRef(false);
  const stepPointer = useRef(false);
  const closeRate = (restore = true) => {
    setRateOpen(false);
    if (restore) {
      if (pointerMenu.current) onPointerComplete?.();
      else triggerRef.current?.focus({ preventScroll: true });
    }
  };
  useEffect(() => {
    if (!rateOpen) return;
    rateRef.current?.querySelector('[aria-checked="true"]')?.focus({ preventScroll: true });
    const outside = (e) => { if (!rateRef.current?.contains(e.target)) setRateOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [rateOpen]);
  const click = (action) => (event) => { action(); if (event.detail) onPointerComplete?.(); };
  return (
    <div className="transport-bar">
      <div className="transport-playback">
        <button type="button" aria-label="後退" onClick={click(() => onStep(-coarseStepSec))}>⏮ −{coarseStepSec}s</button>
        <button type="button" aria-label="後退一格" onClick={click(() => onFrame(-1))}>◀ 格</button>
        <button type="button" aria-label={playing ? "暫停" : "播放"} onClick={click(onTogglePlay)}>{playing ? "⏸" : "▶"}</button>
        <button type="button" aria-label="前進一格" onClick={click(() => onFrame(1))}>格 ▶</button>
        <button type="button" aria-label="前進" onClick={click(() => onStep(coarseStepSec))}>+{coarseStepSec}s ⏭</button>
      </div>
      <label className="transport-step">步幅
        <select aria-label="粗跳步幅" value={coarseStepSec} onPointerDown={() => { stepPointer.current = true; }}
          onKeyDown={() => { stepPointer.current = false; }}
          onChange={(e) => { onCoarseStepChange(Number(e.target.value)); if (stepPointer.current) onPointerComplete?.(); }}>
          {STEP_OPTIONS.map((s) => <option key={s} value={s}>{s}s</option>)}
        </select>
      </label>
      <div className="transport-rate" ref={rateRef} onKeyDown={(e) => {
        if (!rateOpen) return;
        e.stopPropagation();
        if (e.key === "Escape") { e.preventDefault(); closeRate(); }
        else if (e.key === "Tab") { triggerRef.current?.focus({ preventScroll: true }); setRateOpen(false); }
        else if (["ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
          e.preventDefault();
          const items = [...rateRef.current.querySelectorAll('[role="menuitemradio"]')];
          const current = items.indexOf(document.activeElement);
          const index = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : (current + (e.key === "ArrowUp" ? -1 : 1) + items.length) % items.length;
          items[index]?.focus({ preventScroll: true });
        }
      }}>
        <button type="button" ref={triggerRef} aria-label="播放速度" aria-haspopup="menu" aria-expanded={rateOpen}
          onClick={(e) => { pointerMenu.current = e.detail > 0; if (rateOpen) closeRate(); else setRateOpen(true); }}>速度 {playbackRate}x ▴</button>
        {rateOpen && <div className="rate-menu" role="menu" aria-label="選擇播放速度">
          {RATE_OPTIONS.map((rate) => <button key={rate} type="button" role="menuitemradio" aria-checked={playbackRate === rate}
            tabIndex={-1} onClick={() => { onRateChange(rate); closeRate(); }}>{rate}x{rate === playbackRate ? " ✓" : ""}</button>)}
        </div>}
      </div>
      <label className="transport-volume">🔊
        <input aria-label="音量" type="range" min={0} max={1} step={0.05} value={volume}
          onChange={(e) => onVolumeChange(Number(e.target.value))}
          onPointerDown={(e) => { volumePointer.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); }}
          onPointerUp={() => { if (volumePointer.current) onPointerComplete?.(); volumePointer.current = false; }}
          onPointerCancel={() => { volumePointer.current = false; onPointerComplete?.(); }}
          onKeyDown={() => { volumePointer.current = false; }} />
      </label>
      <button type="button" className="transport-fullscreen" aria-label="全螢幕" onClick={click(onFullscreen)}>⛶</button>
    </div>
  );
};
